import { Header } from "@/components/layout/Header";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { 
  ArrowLeft, 
  BookOpen, 
  Activity, 
  BookOpenText, 
  GraduationCap,
  LayoutDashboard,
  UserPlus,
  ClipboardList,
  Award,
  BarChart3
} from "lucide-react";
import { useNavigate } from "react-router-dom";

const GettingStarted = () => {
  const navigate = useNavigate();

  const sections = [
    {
      icon: LayoutDashboard,
      title: "Getting Started with Your Dashboard",
      description: "Your dashboard is the central hub where you can manage your children's profiles. Here you can:",
      features: [
        "Create and manage child profiles",
        "View quick progress summaries",
        "Access recent achievements",
        "See recommended activities"
      ]
    },
    {
      icon: UserPlus,
      title: "Creating Child Profiles",
      description: "Set up individual profiles for each child to:",
      features: [
        "Track development progress separately",
        "Store test results and achievements",
        "Receive age-appropriate recommendations",
        "Document milestones and progress"
      ]
    },
    {
      icon: ClipboardList,
      title: "Child Profile Features",
      description: "Each child's profile contains comprehensive tracking tools:",
      subsections: [
        {
          title: "Overview",
          description: "Quick snapshot of recent achievements, upcoming tests, and progress trends"
        },
        {
          title: "Progress",
          description: "Detailed progress tracking across all developmental areas"
        },
        {
          title: "Testing Modules",
          description: "Access and track completion of age-appropriate assessment modules"
        },
        {
          title: "Test Results",
          description: "Comprehensive history of all completed assessments and scores"
        },
        {
          title: "Milestones",
          description: "Track and celebrate developmental achievements and progress"
        }
      ]
    },
    {
      icon: BookOpen,
      title: "Testing Modules",
      description: "Our Testing Modules provide a research-based approach to assessing early childhood development from infancy to age 6. Covering Motor Skills, Language Skills, Cognitive Development, Independent Skills, and Social & Emotional Skills, these assessments offer parents valuable insights into their child's growth. With 875 research-backed questions, the modules are short, easy, and engaging, making it simple to track progress. Some include visual aids, while most encourage interactive, parent-led evaluations for deeper understanding. Since children develop at their own pace, parents can progress through different stages as needed and retest later to monitor improvement. These stages serve as a guideline to support development, helping parents celebrate milestones and foster growth at every stage.",
      note: "For the best experience, we recommend accessing the modules through your Child's Profile, where you can view their complete testing history. Alternatively, you can access all testing modules directly through the Testing Modules page."
    }
  ];

  const features = [
    {
      icon: BookOpen,
      title: "Testing Modules",
      description: "Access comprehensive testing modules through the child's profile. Track completion status and progress for each assessment.",
      path: "/testing-modules"
    },
    {
      icon: Activity,
      title: "Activities and Goals",
      description: "Discover key milestones and age-based activities tailored to each developmental category.",
      path: "/activities-and-goals"
    },
    {
      icon: BookOpenText,
      title: "Journal",
      description: "Document your child's journey, milestones, and special moments in a dedicated digital journal.",
      path: "/journal"
    }
  ];

  const handleNavigation = (path: string) => {
    window.scrollTo(0, 0);
    navigate(path);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <main className="container mx-auto px-4 py-8">
        <Button
          variant="ghost"
          onClick={() => handleNavigation("/dashboard")}
          className="mb-4"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Dashboard
        </Button>

        <div className="text-center mb-12">
          <div className="flex justify-center mb-4">
            <GraduationCap className="h-16 w-16 text-primary" />
          </div>
          <h1 className="text-3xl font-heading font-bold text-gray-900 mb-4">
            Welcome to Bright Stages
          </h1>
          <p className="text-gray-600 max-w-2xl mx-auto">
            Let's walk through how to get started and make the most of your child's developmental journey.
          </p>
        </div>

        {/* Getting Started Guide */}
        <div className="space-y-8 mb-12">
          {sections.map((section, index) => (
            <Card key={index} className="overflow-hidden">
              <CardHeader className="border-b bg-gray-50">
                <div className="flex items-center gap-3">
                  <section.icon className="h-6 w-6 text-primary" />
                  <CardTitle className="text-xl">{section.title}</CardTitle>
                </div>
              </CardHeader>
              <CardContent className="p-6">
                <p className="text-gray-600 mb-4">{section.description}</p>
                {section.features && (
                  <ul className="space-y-2">
                    {section.features.map((feature, featureIndex) => (
                      <li key={featureIndex} className="flex items-center gap-2">
                        <div className="h-1.5 w-1.5 rounded-full bg-primary" />
                        <span className="text-gray-700">{feature}</span>
                      </li>
                    ))}
                  </ul>
                )}
                {section.subsections && (
                  <div className="grid gap-4 mt-4">
                    {section.subsections.map((subsection, subIndex) => (
                      <div key={subIndex} className="bg-gray-50 rounded-lg p-4">
                        <h4 className="font-medium text-gray-900 mb-1">{subsection.title}</h4>
                        <p className="text-sm text-gray-600">{subsection.description}</p>
                      </div>
                    ))}
                  </div>
                )}
                {section.note && (
                  <p className="text-sm text-gray-600 mt-4 bg-blue-50 p-4 rounded-lg">
                    {section.note}
                  </p>
                )}
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Key Features */}
        <div className="space-y-4 mb-8">
          <h2 className="text-2xl font-bold text-center">Key Features</h2>
          <p className="text-gray-600 text-center mb-6">
            Explore these core features to support your child's development
          </p>
          <div className="grid gap-6 md:grid-cols-3">
            {features.map((feature, index) => (
              <Card 
                key={index} 
                className="hover:shadow-lg transition-shadow cursor-pointer"
                onClick={() => handleNavigation(feature.path)}
              >
                <CardHeader>
                  <div className="flex justify-center mb-4">
                    <feature.icon className="h-12 w-12 text-primary" />
                  </div>
                  <CardTitle className="text-xl text-center">{feature.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-center text-gray-600">{feature.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* Get Started Button */}
        <div className="text-center mt-12">
          <Button
            size="lg"
            onClick={() => handleNavigation("/dashboard")}
            className="gap-2"
          >
            <LayoutDashboard className="h-5 w-5" />
            Go to Dashboard
          </Button>
        </div>
      </main>
    </div>
  );
};

export default GettingStarted;
